import type { components } from '@multaqa/api-client'
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { api } from '@/lib/api'

export type TaxRate = components['schemas']['TaxRateResource']
export type PaymentTerm = Omit<
  components['schemas']['PaymentTermResource'],
  'is_system' | 'sort_order'
> &
  Partial<Pick<components['schemas']['PaymentTermResource'], 'is_system' | 'sort_order'>>
export type TaxAgency = {
  id: string
  name: Record<string, string>
  reference: string | null
  filing_frequency: string | null
  filing_day: number | null
  is_active: boolean
}
export type TaxFiling = {
  id: string
  period_start: string
  period_end: string
  status: 'open' | 'filed'
  tax_agency_id: string
  tax_agency?: TaxAgency
  tax_collected: string
  tax_paid: string
  net_due: string
  reference: string | null
}
export type TaxAssignment = components['schemas']['TaxRateAssignmentResource']

const unwrap = <T>(response: { data?: unknown }): T[] => {
  const outer = response.data as { data?: unknown } | undefined
  return (outer?.data ?? []) as T[]
}
const headers = (companyId: string) => ({ headers: { 'X-Company-Id': companyId } })

export function useTaxCentre<T>(
  companyId: string | null,
  endpoint:
    | '/accounting/payment-terms'
    | '/accounting/tax-agencies'
    | '/accounting/tax-filings'
    | '/accounting/tax-rate-assignments',
  key: string,
  options?: Omit<UseQueryOptions<T[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<T[], Error>({
    queryKey: ['companies', companyId, 'accounting', key],
    enabled: Boolean(companyId),
    ...options,
    queryFn: async () => {
      if (!companyId) return []
      const response = (await api.GET(endpoint, headers(companyId))) as {
        error?: { message?: string }
        data?: unknown
      }
      if (response.error) throw new Error(response.error.message ?? `Failed to load ${key}`)
      return unwrap<T>(response)
    },
  })
}

export const usePaymentTerms = (id: string | null) =>
  useTaxCentre<PaymentTerm>(id, '/accounting/payment-terms', 'payment-terms')
export const useTaxAgencies = (id: string | null) =>
  useTaxCentre<TaxAgency>(id, '/accounting/tax-agencies', 'tax-agencies')
export const useTaxFilings = (id: string | null) =>
  useTaxCentre<TaxFiling>(id, '/accounting/tax-filings', 'tax-filings')
export const useTaxAssignments = (id: string | null) =>
  useTaxCentre<TaxAssignment>(id, '/accounting/tax-rate-assignments', 'tax-assignments')

export function useTaxFilingAction(
  companyId: string | null,
  action: 'file' | 'reopen',
  options?: Omit<
    UseMutationOptions<TaxFiling, Error, { id: string; reference?: string }>,
    'mutationFn'
  >,
) {
  const client = useQueryClient()
  return useMutation<TaxFiling, Error, { id: string; reference?: string }>({
    ...options,
    mutationFn: async ({ id, reference }) => {
      if (!companyId) throw new Error('No active company')
      const endpoint =
        action === 'file'
          ? ('/accounting/tax-filings/{id}/file' as const)
          : ('/accounting/tax-filings/{id}/reopen' as const)
      const response = (await api.POST(endpoint, {
        ...headers(companyId),
        params: { path: { id } },
        body: action === 'file' ? { reference } : undefined,
      })) as { error?: { message?: string }; data?: unknown }
      if (response.error) throw new Error(response.error.message ?? `Failed to ${action} filing`)
      return (response.data as { data: TaxFiling }).data
    },
    onSuccess: (...args) => {
      void client.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting', 'tax-filings'],
      })
      options?.onSuccess?.(...args)
    },
  })
}

export function useSavePaymentTerm(
  companyId: string | null,
  options?: Omit<UseMutationOptions<PaymentTerm, Error, Partial<PaymentTerm>>, 'mutationFn'>,
) {
  const client = useQueryClient()
  return useMutation<PaymentTerm, Error, Partial<PaymentTerm>>({
    ...options,
    mutationFn: async (body) => {
      if (!companyId) throw new Error('No active company')
      const response = (await api.POST('/accounting/payment-terms', {
        ...headers(companyId),
        body: body as unknown as components['schemas']['PaymentTermRequest'],
      })) as { error?: { message?: string }; data?: unknown }
      if (response.error) throw new Error(response.error.message ?? 'Failed to save payment term')
      return (response.data as { data: PaymentTerm }).data
    },
    onSuccess: (...args) => {
      void client.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting', 'payment-terms'],
      })
      options?.onSuccess?.(...args)
    },
  })
}

function useAccountingMutation<T, V>(
  companyId: string | null,
  invalidate: string,
  request: (body: V) => Promise<T>,
  options?: Omit<UseMutationOptions<T, Error, V>, 'mutationFn'>,
) {
  const client = useQueryClient()
  return useMutation<T, Error, V>({
    ...options,
    mutationFn: request,
    onSuccess: (...args) => {
      void client.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting', invalidate],
      })
      options?.onSuccess?.(...args)
    },
  })
}

export type PaymentTermBody = Omit<PaymentTerm, 'id'>
export const useUpdatePaymentTerm = (
  companyId: string | null,
  options?: Omit<
    UseMutationOptions<PaymentTerm, Error, { id: string; body: Partial<PaymentTermBody> }>,
    'mutationFn'
  >,
) =>
  useAccountingMutation(
    companyId,
    'payment-terms',
    async ({ id, body }) => {
      const response = (await api.PATCH('/accounting/payment-terms/{id}', {
        ...headers(companyId ?? ''),
        params: { path: { id } },
        body: body as unknown as components['schemas']['PaymentTermRequest'],
      })) as { error?: { message?: string }; data?: unknown }
      if (response.error) throw new Error(response.error.message ?? 'Failed to update payment term')
      return (response.data as { data: PaymentTerm }).data
    },
    options,
  )

export const useDeletePaymentTerm = (
  companyId: string | null,
  options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>,
) =>
  useAccountingMutation(
    companyId,
    'payment-terms',
    async (id) => {
      const response = (await api.DELETE('/accounting/payment-terms/{id}', {
        ...headers(companyId ?? ''),
        params: { path: { id } },
      })) as { error?: { message?: string } }
      if (response.error)
        throw new Error(response.error.message ?? 'Failed to deactivate payment term')
    },
    options,
  )

export type TaxAgencyBody = {
  name: Record<string, string>
  reference?: string | null
  filing_frequency?: string
  filing_day?: number | null
  is_active?: boolean
}
export const useSaveTaxAgency = (
  companyId: string | null,
  options?: Omit<
    UseMutationOptions<TaxAgency, Error, { id?: string; body: TaxAgencyBody }>,
    'mutationFn'
  >,
) =>
  useAccountingMutation(
    companyId,
    'tax-agencies',
    async ({ id, body }) => {
      const response = (await (id
        ? api.PATCH('/accounting/tax-agencies/{id}', {
            ...headers(companyId ?? ''),
            params: { path: { id } },
            body: body as unknown as components['schemas']['TaxAgencyRequest'],
          })
        : api.POST('/accounting/tax-agencies', {
            ...headers(companyId ?? ''),
            body: body as unknown as components['schemas']['TaxAgencyRequest'],
          }))) as { error?: { message?: string }; data?: unknown }
      if (response.error) throw new Error(response.error.message ?? 'Failed to save tax agency')
      return (response.data as { data: TaxAgency }).data
    },
    options,
  )

export type TaxFilingBody = components['schemas']['TaxFilingRequest']
export const useCreateTaxFiling = (
  companyId: string | null,
  options?: Omit<UseMutationOptions<TaxFiling, Error, TaxFilingBody>, 'mutationFn'>,
) =>
  useAccountingMutation(
    companyId,
    'tax-filings',
    async (body) => {
      const response = (await api.POST('/accounting/tax-filings', {
        ...headers(companyId ?? ''),
        body,
      })) as { error?: { message?: string }; data?: unknown }
      if (response.error) throw new Error(response.error.message ?? 'Failed to create filing')
      return (response.data as { data: TaxFiling }).data
    },
    options,
  )
